import React, { useMemo } from 'react';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router-dom';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { DataTable } from '../components/DataTable';
import { SelectDropdown } from '../components/SelectDropdown';
import { PageHeader } from '../components/PageHeader';
import { TabBar } from '../components/TabBar';
import { useWebDetailed } from 'apiTyped/hooks/onlineSales.query';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PriceReference {
  id?: string | number;
  wine_name: string;
  store_name: string;
  market: string;
  vintage: string;
  price: number;
  bottle_format: string;
  currency: string;
  date: string;
}

interface PriceHistoryPoint {
  date: string;
  wine_name: string;
  market_name: string;
  price: number;
  vintage: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const OnlineSalesDetailed: React.FC<RouteComponentProps> = ({
  history
}) => {
  const [selectedWine, setSelectedWine] = React.useState<string>('');
  const [selectedMarket, setSelectedMarket] = React.useState<string>('');
  const [selectedVintage, setSelectedVintage] = React.useState<string>('');
  const [selectedFormat, setSelectedFormat] = React.useState<string>('750');

  const { isLoading, webDetailedData } = useWebDetailed({
    vintage: selectedVintage || undefined,
    wines: selectedWine || undefined,
    market: selectedMarket || undefined
  });

  const {
    kpis = [],
    feed = [],
    prices = [],
    references = [],
    price_history = []
  } = webDetailedData as Record<string, any>;

  // Derive filter options from data
  const wineOptions = useMemo(() => {
    const filters = (webDetailedData as any)?.filters || {};
    const clientWines: { wine_name: string }[] = filters.client_wines || [];
    return clientWines.map((w) => ({ value: w.wine_name, label: w.wine_name }));
  }, [webDetailedData]);

  const marketOptions = useMemo(() => {
    const filters = (webDetailedData as any)?.filters || {};
    const markets: string[] = filters.markets || [];
    return markets.map((m) => ({ value: m, label: m }));
  }, [webDetailedData]);

  const vintageOptions = useMemo(() => {
    const allVintages = [...new Set((prices as any[]).map((p) => p.vintage))]
      .sort()
      .reverse();
    return allVintages.map((v) => ({ value: v, label: v }));
  }, [prices]);

  // Filter references by format
  const filteredReferences = useMemo(() => {
    return (references as PriceReference[]).filter((ref) => {
      if (selectedFormat === 'all') {
        return (
          ref.bottle_format !== '750' &&
          ref.bottle_format !== '1500' &&
          ref.bottle_format !== '3000'
        );
      }
      return ref.bottle_format === selectedFormat;
    });
  }, [references, selectedFormat]);

  // KPI aggregation
  const refCount = filteredReferences.length;
  const avgPrice = useMemo(() => {
    if (!filteredReferences.length) return '—';
    const avg =
      filteredReferences.reduce((s, r) => s + (r.price || 0), 0) /
      filteredReferences.length;
    return `€${avg.toFixed(0)}`;
  }, [filteredReferences]);

  const minPrice = useMemo(() => {
    if (!filteredReferences.length) return '—';
    const min = Math.min(...filteredReferences.map((r) => r.price || Infinity));
    return min === Infinity ? '—' : `€${min.toFixed(0)}`;
  }, [filteredReferences]);

  const maxPrice = useMemo(() => {
    if (!filteredReferences.length) return '—';
    const max = Math.max(...filteredReferences.map((r) => r.price || 0));
    return max === 0 ? '—' : `€${max.toFixed(0)}`;
  }, [filteredReferences]);

  // Price history simplified (latest per store)
  const priceByStore = useMemo(() => {
    const map = new Map<
      string,
      { store: string; price: number; date: string }
    >();
    (price_history as PriceHistoryPoint[]).forEach((p) => {
      const existing = map.get(p.wine_name);
      if (!existing || p.date > existing.date) {
        map.set(p.wine_name, {
          store: p.wine_name,
          price: p.price,
          date: p.date
        });
      }
    });
    return Array.from(map.values()).slice(0, 15);
  }, [price_history]);

  // References table columns
  const refColumns = useMemo(
    () => [
      {
        key: 'store_name',
        header: 'Store',
        render: (row: PriceReference) => (
          <StoreName>{row.store_name}</StoreName>
        ),
        width: '22%'
      },
      {
        key: 'market',
        header: 'Market',
        render: (row: PriceReference) => row.market
      },
      {
        key: 'vintage',
        header: 'Vintage',
        render: (row: PriceReference) => row.vintage,
        sortable: true
      },
      {
        key: 'price',
        header: 'Price',
        render: (row: PriceReference) =>
          `${row.currency || '€'}${(row.price || 0).toFixed(0)}`,
        sortable: true
      },
      {
        key: 'bottle_format',
        header: 'Format',
        render: (row: PriceReference) => `${row.bottle_format}ml`
      },
      {
        key: 'date',
        header: 'Last Seen',
        render: (row: PriceReference) => row.date || '—'
      }
    ],
    []
  );

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Online Sales"
        subtitle="Detailed view — drill into pricing references, price history, and individual retailer performance per vintage."
      />

      {/* Tabs */}
      <TabBar
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'detailed', label: 'Detailed' }
        ]}
        activeTab="detailed"
        onTabChange={(id) => {
          if (id === 'overview') history.push('/v2/campaigns/online-sales');
        }}
      />

      {/* Filters */}
      <FiltersRow>
        <FilterPill>
          <PillLabel>Wine</PillLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All wines' }, ...wineOptions]}
            value={selectedWine}
            onChange={(val) => setSelectedWine(val)}
            disabled={isLoading}
          />
        </FilterPill>
        <FilterPill>
          <PillLabel>Market</PillLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All markets' }, ...marketOptions]}
            value={selectedMarket}
            onChange={(val) => setSelectedMarket(val)}
            disabled={isLoading}
          />
        </FilterPill>
        <FilterPill>
          <PillLabel>Vintage</PillLabel>
          <SelectDropdown
            options={[{ value: '', label: 'All vintages' }, ...vintageOptions]}
            value={selectedVintage}
            onChange={(val) => setSelectedVintage(val)}
            disabled={isLoading}
          />
        </FilterPill>
        <FormatToggle>
          {[
            { label: '750ml', value: '750' },
            { label: '1500ml', value: '1500' },
            { label: '3000ml', value: '3000' },
            { label: 'Other', value: 'all' }
          ].map((f) => (
            <FormatBtn
              key={f.value}
              $active={selectedFormat === f.value}
              onClick={() => setSelectedFormat(f.value)}
            >
              {f.label}
            </FormatBtn>
          ))}
        </FormatToggle>
      </FiltersRow>

      {/* KPI Strip */}
      <KpiStrip
        items={[
          {
            label: 'References Found',
            value: refCount.toLocaleString(),
            delta: {
              value: `${
                selectedFormat === 'all'
                  ? 'Other formats'
                  : selectedFormat + 'ml'
              }`,
              direction: 'flat'
            }
          },
          {
            label: 'Average Price',
            value: avgPrice,
            delta: { value: 'Current selection', direction: 'flat' }
          },
          {
            label: 'Min Price',
            value: minPrice,
            delta: { value: 'Lowest observed', direction: 'down' }
          },
          {
            label: 'Max Price',
            value: maxPrice,
            delta: { value: 'Highest observed', direction: 'up' }
          }
        ]}
      />

      {isLoading && <LoadingBar>Loading data…</LoadingBar>}

      {/* Price chart (simplified bar view) */}
      {priceByStore.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitle>Price Comparison</SectionTitle>
            <SectionMeta>Latest price per retailer / reference</SectionMeta>
          </SectionHeader>
          <BarChartGrid>
            {priceByStore.map((item) => (
              <BarItem key={item.store}>
                <BarLabelText>{item.store}</BarLabelText>
                <BarTrack>
                  <BarFill
                    style={{
                      width: `${Math.min(
                        100,
                        ((item.price || 0) /
                          Math.max(...priceByStore.map((d) => d.price || 0))) *
                          100
                      )}%`
                    }}
                  />
                </BarTrack>
                <BarValue>€{(item.price || 0).toFixed(0)}</BarValue>
              </BarItem>
            ))}
          </BarChartGrid>
        </Section>
      )}

      {/* References Table */}
      <Section>
        <SectionHeader>
          <SectionTitle>Price References</SectionTitle>
          <SectionMeta>
            {refCount} references for{' '}
            {selectedFormat === 'all' ? 'other formats' : `${selectedFormat}ml`}
          </SectionMeta>
        </SectionHeader>
        <DataTable
          columns={refColumns}
          data={filteredReferences.map((r, i) => ({ ...r, id: r.id ?? i }))}
          emptyMessage="No references match the current filters."
        />
      </Section>
    </Page>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 0;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PillLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const FormatToggle = styled.div`
  display: flex;
  gap: 2px;
  background: var(--paper-warm);
  border-radius: var(--radius-sm);
  padding: 2px;
  margin-left: auto;
`;

const FormatBtn = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? 'var(--white)' : 'transparent')};
  border: none;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? 'var(--ink)' : 'var(--ink-muted)')};
  border-radius: var(--radius-sm);
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow-sm)' : 'none')};
  transition: all 0.15s;

  &:hover {
    color: var(--ink);
  }
`;

const LoadingBar = styled.div`
  text-align: center;
  padding: 24px;
  color: var(--ink-muted);
  font-size: 13px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  margin-bottom: 12px;
`;

const SectionTitle = styled.h3`
  font-family: var(--font-sans-bold);
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 2px;
`;

const SectionMeta = styled.p`
  font-size: 12px;
  color: var(--ink-faint);
  margin: 0;
`;

const StoreName = styled.span`
  font-weight: 600;
  color: var(--ink);
`;

// Simple horizontal bar chart
const BarChartGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  padding: 16px;
`;

const BarItem = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr 60px;
  align-items: center;
  gap: 12px;
`;

const BarLabelText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  height: 8px;
  background: var(--paper-warm);
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: var(--terracotta);
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const BarValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  text-align: right;
`;
